import React, { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_COWORK_MCP_CONNECTOR } from '../../graphql/queries';
import { useCopy } from '../../lib/copyToClipboard';
import { AGENT_ACTIVITY } from './AgentStatus';

/**
 * "New page" / "New form" on Content (Prototype v7): the agent builds pages and
 * forms, so the button opens a small panel that says so, with a ready prompt to
 * copy into the agent and the way to the agent: its name when one is connected,
 * "Connect an agent" when none is. Both lead to Settings → Connections.
 */
const COPY = {
  page: {
    label: 'New page',
    head: 'Your agent builds pages',
    prompt:
      'Build a new page called “About us” using my site’s template, and send it to me for approval in tinycms.',
  },
  form: {
    label: 'New form',
    head: 'Your agent builds forms',
    prompt:
      'Create a sign-up form (name and email) and place it on the page I name. Send it to me for approval in tinycms.',
  },
} as const;

const CONNECTIONS = '/settings?section=integrations';

export function AgentNewPanel({ kind }: { kind: 'page' | 'form' }) {
  const { accountId } = useAccount();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const panelId = useId();
  const { copy, state } = useCopy();
  const text = COPY[kind];

  const { data: activity } = useQuery<{ agentActivity: { connected: boolean } }>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId || !open,
    fetchPolicy: 'cache-first',
  });
  const connected = activity?.agentActivity.connected;
  const { data: connector } = useQuery<{ coworkMcpConnector: { label?: string | null } | null }>(
    GET_COWORK_MCP_CONNECTOR,
    { variables: { accountId }, skip: !accountId || !open || !connected }
  );
  const agentName = connector?.coworkMcpConnector?.label?.trim();

  // Close on Escape or a click outside.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const copied = state('prompt');
  return (
    <span className="pg-agent-new" ref={wrap}>
      <button
        type="button"
        className="ink-btn ink-btn-primary"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {text.label}
      </button>
      {open ? (
        <div className="pg-agent-panel" id={panelId} role="dialog" aria-label={text.head}>
          <div className="pg-agent-panel-head">{text.head}</div>
          <p>
            Copy this prompt into your agent. What it builds comes back here for you to publish.
          </p>
          <div className="pg-agent-prompt">{text.prompt}</div>
          <div className="pg-agent-panel-foot">
            {connected === false ? (
              <Link to={CONNECTIONS}>Connect an agent</Link>
            ) : (
              <Link to={CONNECTIONS}>
                {agentName ? `Your agent: ${agentName} →` : 'Your agent →'}
              </Link>
            )}
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={(e) => copy('prompt', text.prompt, e)}
            >
              {copied === 'copied'
                ? 'Copied'
                : copied === 'failed'
                  ? 'Couldn’t copy'
                  : 'Copy prompt'}
            </button>
          </div>
        </div>
      ) : null}
    </span>
  );
}
